import type {
  EzremsBindingCandidate,
  EzremsBindingTarget,
} from '@/api/core/ezrems-binding-context';
import {
  bindEzremsUnit,
  parseEzremsUnitBindingReceipt,
  readEzremsUnitBindingContext,
  validateEzremsUnitBindingCommand,
  type EzremsUnitBindingCommand,
  type EzremsUnitBindingContext,
} from '@/api/core/ezrems-unit-binding';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useRef, useState } from 'react';
import { currentAccess } from './ezrems-room-binding-section';
import { freezeRequest } from './presentation/command-owner';
import type { PropertyRuntime } from './property-queries';

/**
 * 슬롯(Unit) 단위 이지렘스 최초 연결. 로컬 BE candidate(010, 미배포) 계약을 따른다.
 * Unit POST는 최초 연결(expected_binding_revision 0)만 받으므로 재연결·해제 동작은 없다.
 * Room 연결 화면과 같은 원칙: 사용자가 연 뒤에만 읽고, 원 요청·수행자·CAS를 고정하며, 결과 불명은 자동 재전송하지 않는다.
 */
interface Props {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId: string;
  roomId: string;
  roomName: string;
  unit: { id: string; name: string };
  onBindingChanged?: () => void;
  /** 아직 저장하지 않은 입력이 있는지 알린다(화면을 떠날 때 경고하는 데 쓴다). */
  onDirtyChange?: (dirty: boolean) => void;
}
interface Intent {
  unitId: string;
  actorId: string;
  generation: number;
  command: EzremsUnitBindingCommand;
}
const snapshot = (context: EzremsUnitBindingContext) =>
  JSON.stringify({
    unit_id: context.unit_id,
    unit_version: context.unit_version,
    room_id: context.room_id,
    room_version: context.room_version,
    binding_revision: context.binding_revision,
    binding: context.binding,
    scope: context.scope,
    targets: context.targets,
  });

export function EzremsUnitBindingSection(props: Props) {
  const auth = useOperatorSession(props.runtime.session);
  const allowed = currentAccess(props.runtime, props.generation);
  const [context, setContext] = useState<EzremsUnitBindingContext | null>(null);
  const [target, setTarget] = useState<EzremsBindingTarget | null>(null);
  const [candidates, setCandidates] = useState<EzremsBindingCandidate[]>([]);
  const [candidateId, setCandidateId] = useState('');
  const [reason, setReason] = useState('');
  const reasonTooLong = Array.from(reason).length > 500;
  const reasonErrorId = useId();
  const [intent, setIntent] = useState<Intent | null>(null);
  const [held, setHeld] = useState<Intent | null>(null);
  const [heldRefused, setHeldRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  // 저장 성공 안내. 오류 안내(feedback)와 달리 "다시 조회" 버튼을 부르지 않는다.
  const [savedNotice, setSavedNotice] = useState('');
  const [done, setDone] = useState(false);
  const alive = useRef(true);
  const epoch = useRef(0);
  const lock = useRef(false);
  const originals = useRef(new WeakMap<Intent, string>());
  const ownerKey = JSON.stringify([
    props.runtime.apiNamespace,
    props.generation,
    props.propertyId,
    props.typeId,
    props.roomId,
    props.unit.id,
    auth.generation,
    auth.status,
    auth.authorization,
    auth.deniedDomains,
  ]);
  const owner = useRef(ownerKey);
  owner.current = ownerKey;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      epoch.current += 1;
    };
  }, []);
  const dirty =
    !!context && !held && !done && (reason !== '' || candidateId !== '' || target !== null);
  useEffect(() => {
    props.onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 입력이 바뀔 때만 알린다.
  }, [dirty]);
  useEffect(
    () => () => {
      props.onDirtyChange?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면이 사라질 때만 해제한다.
    [],
  );
  const valid = () => alive.current && currentAccess(props.runtime, props.generation);
  const clear = () => {
    setIntent(null);
    setCandidates([]);
    setCandidateId('');
    setTarget(null);
  };
  const check = (data: EzremsUnitBindingContext) => {
    const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
    if (
      !same(data.scope.property_id, props.propertyId) ||
      !same(data.scope.room_type_id, props.typeId) ||
      !same(data.scope.room_id, props.roomId)
    )
      throw new Error('Changed parent');
  };
  const failure = (error: unknown) => {
    if (!valid()) return;
    clear();
    setContext(null);
    setFeedback(
      isApiError(error) && error.status === 404
        ? '슬롯 연결 정보를 찾을 수 없습니다. 이 서버가 슬롯 단위 연결을 지원하는지 확인하세요.'
        : isApiError(error) && error.status === 503
          ? 'EZREMS 연결 정보와 후보를 현재 조회할 수 없습니다. 다시 조회하고 후보를 새로 선택하세요.'
          : '연결 정보를 다시 조회하고 후보를 새로 선택하세요.',
    );
    if (isApiError(error) && error.status === 403)
      props.runtime.session.reportDomainAccessDenied('property', props.generation);
  };
  const reload = async (keepFeedback = false) => {
    if (!valid() || lock.current) return;
    const request = ++epoch.current;
    clear();
    setContext(null);
    setBusy(true);
    if (!keepFeedback) setFeedback('');
    try {
      const data = await readEzremsUnitBindingContext(props.runtime.client, props.unit.id);
      check(data);
      if (valid() && request === epoch.current) setContext(data);
    } catch (error) {
      if (request === epoch.current) failure(error);
    } finally {
      if (alive.current && request === epoch.current) setBusy(false);
    }
  };
  const allCandidates = async (
    selected: EzremsBindingTarget,
    baseline: EzremsUnitBindingContext,
  ) => {
    const requestEpoch = epoch.current;
    let cursor: number | undefined;
    const found: EzremsBindingCandidate[] = [];
    for (let page = 0; page < 1000; page += 1) {
      const data = await readEzremsUnitBindingContext(props.runtime.client, props.unit.id, {
        ...selected,
        ...(cursor === undefined ? {} : { after_resource_id: cursor }),
      });
      if (!valid() || requestEpoch !== epoch.current) throw new Error('Scope left');
      check(data);
      if (snapshot(data) !== snapshot(baseline)) throw new Error('Scope changed');
      found.push(...data.candidates);
      if (data.next_resource_id === null) return { data, found };
      cursor = data.next_resource_id;
    }
    throw new Error('Incomplete candidates');
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 슬롯을 바꾸면 부모가 key로 새로 마운트한다.
  }, []);
  const selectTarget = async (value: string) => {
    const selected = context?.targets[Number(value)];
    clear();
    setFeedback('');
    const request = ++epoch.current;
    if (value === '' || !selected || !valid()) return;
    setTarget(selected);
    setBusy(true);
    try {
      const result = await allCandidates(selected, context);
      if (valid() && request === epoch.current) {
        setContext(result.data);
        setCandidates(result.found);
      }
    } catch (error) {
      if (request === epoch.current) failure(error);
    } finally {
      if (alive.current && request === epoch.current) setBusy(false);
    }
  };
  const review = async () => {
    if (!target || !context || busy || sending || held !== null || !valid()) return;
    const candidate = candidates.find((c) => String(c.resource_id) === candidateId);
    if (!candidate || !reason.trim() || reasonTooLong || context.binding_revision !== 0) return;
    const requestEpoch = ++epoch.current;
    setBusy(true);
    setIntent(null);
    setFeedback('');
    setSavedNotice('');
    try {
      const result = await allCandidates(target, context);
      if (!result.found.some((c) => JSON.stringify(c) === JSON.stringify(candidate)))
        throw new Error('Candidate changed');
      const actor = await props.runtime.client.get<{ user_id?: unknown }>('users/me');
      if (!valid() || requestEpoch !== epoch.current || !isUuid(actor.user_id))
        throw new Error('Actor changed');
      setIntent({
        unitId: props.unit.id,
        actorId: actor.user_id,
        generation: props.generation,
        command: {
          command_id: crypto.randomUUID(),
          expected_unit_version: context.unit_version,
          expected_binding_revision: 0,
          scope: { ...context.scope },
          ...target,
          resource_id: candidate.resource_id,
          dong: candidate.dong,
          floor: candidate.floor,
          ho2: candidate.ho2,
          reason,
        },
      });
    } catch (error) {
      if (requestEpoch === epoch.current) failure(error);
    } finally {
      if (alive.current && requestEpoch === epoch.current) setBusy(false);
    }
  };
  const send = async (target_: Intent) => {
    if (lock.current) return;
    if (!currentAccess(props.runtime, target_.generation)) {
      setFeedback('현재 권한을 확인할 수 없어 저장하지 않았습니다. 연결 정보를 다시 조회하세요.');
      return;
    }
    lock.current = true;
    setSending(true);
    const startedOwner = ownerKey;
    const replaying = held === target_;
    let attempted = false;
    try {
      const actor = await props.runtime.client.get<{ user_id?: unknown }>('users/me');
      if (
        owner.current !== startedOwner ||
        actor.user_id !== target_.actorId ||
        !currentAccess(props.runtime, target_.generation)
      ) {
        setFeedback('원래 수행자와 권한을 확인할 수 없습니다. 원래 요청을 보존했습니다.');
        return;
      }
      if (replaying) {
        const originalJson = originals.current.get(target_);
        if (originalJson === undefined || originalJson !== JSON.stringify(target_.command))
          throw new Error('원래 연결 요청이 변경되었습니다.');
        attempted = true;
        parseEzremsUnitBindingReceipt(
          await props.runtime.client.post<unknown>(`admin/units/${target_.unitId}/ezrems-binding`, {
            json: target_.command,
            expectedStatus: 200,
          }),
          target_.unitId,
          target_.command,
        );
      } else {
        validateEzremsUnitBindingCommand(target_.unitId, target_.command);
        freezeRequest(target_.command);
        originals.current.set(target_, JSON.stringify(target_.command));
        setHeld(target_);
        attempted = true;
        await bindEzremsUnit(props.runtime.client, target_.unitId, target_.command);
      }
      setHeld(null);
      setHeldRefused(false);
      if (owner.current === startedOwner) {
        setDone(true);
        props.onBindingChanged?.();
        // 불명확했던 이전 시도의 안내가 성공 뒤에 남지 않게 한다.
        setFeedback('');
        setSavedNotice('슬롯을 EZREMS 호실에 연결했습니다.');
        lock.current = false;
        setSending(false);
        await reload(true);
      }
    } catch (error) {
      const unknown = !isApiError(error) || error.kind !== 'http' || (error.status ?? 500) >= 500;
      if (attempted && !unknown && !replaying) setHeld(null);
      const refusedReplay = attempted && replaying && isApiError(error) && error.status === 409;
      if (refusedReplay) setHeldRefused(true);
      if (owner.current === startedOwner) {
        if (isApiError(error) && error.status === 403)
          props.runtime.session.reportDomainAccessDenied('property', props.generation);
        setFeedback(
          refusedReplay
            ? '서버가 같은 요청을 현재 연결 상태와 맞지 않는다며 거절했습니다. 다른 변경이 먼저 반영됐거나 원래 요청이 이미 반영됐을 수 있어, 새 요청은 보내지 않았습니다. 요청을 폐기하고 현재 연결을 다시 조회해 확인하세요.'
            : (attempted && unknown) || (attempted && replaying)
              ? '연결 결과가 불명확합니다. 처음 요청과 입력을 보존했으며 자동 재전송하지 않습니다.'
              : '연결 정보를 다시 조회하고 후보를 새로 선택하세요.',
        );
        if (!(attempted && (unknown || replaying))) {
          clear();
          setContext(null);
        }
      }
    } finally {
      lock.current = false;
      setSending(false);
    }
  };
  const blocked = held !== null || sending || done;
  const connected = (context?.binding_revision ?? 0) > 0;
  return (
    <section
      aria-label="슬롯 최초 연결 확인 패널"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <p>
        {props.roomName} · {props.unit.name} 슬롯을 EZREMS 호실에 연결합니다. 슬롯 연결은 최초
        연결만 지원하며, 객실 전체 연결과는 함께 쓸 수 없습니다.
      </p>
      {!allowed && <p role="status">현재 지점 연결 권한이 없습니다.</p>}
      {busy && <p role="status">연결 정보를 불러오고 있습니다.</p>}
      {context?.binding && (
        <p>
          현재 연결: {context.binding.branch_code} · {context.binding.project_code} ·{' '}
          {context.binding.dong} · {context.binding.floor} · {context.binding.ho2}
        </p>
      )}
      {context && !connected && <p>아직 연결하지 않은 슬롯입니다.</p>}
      {context && !connected && context.targets.length === 0 && (
        <p role="status">연결할 EZREMS 지점이 설정되지 않았습니다. 지점 설정을 확인해 주세요.</p>
      )}
      {context && !connected && allowed && !blocked && (
        <>
          <div className="grid gap-1 sm:max-w-sm">
            EZREMS 프로젝트
            <Select
              disabled={busy || intent !== null || context.targets.length === 0}
              value={
                target
                  ? String(
                      context.targets.findIndex(
                        (t) => JSON.stringify(t) === JSON.stringify(target),
                      ),
                    )
                  : null
              }
              onValueChange={(value) => {
                void selectTarget(value ?? '');
              }}
              items={context.targets.map((t, i) => ({
                value: String(i),
                label: `${t.branch_code} · ${t.project_code}`,
              }))}
            >
              <SelectTrigger aria-label="슬롯 EZREMS 프로젝트">
                <SelectValue placeholder="프로젝트 선택" />
              </SelectTrigger>
              <SelectContent>
                {context.targets.map((t, i) => (
                  <SelectItem key={`${String(t.branch_id)}:${t.project_code}`} value={String(i)}>
                    {t.branch_code} · {t.project_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1 sm:max-w-sm">
            EZREMS 연결 후보
            <Select
              disabled={busy || intent !== null || !target || candidates.length === 0}
              value={candidateId || null}
              onValueChange={(value) => {
                setCandidateId(value ?? '');
                setIntent(null);
              }}
              items={candidates.map((c) => ({
                value: String(c.resource_id),
                label: `${c.dong} · ${c.floor} · ${c.ho2} · ${c.sales_kind}`,
              }))}
            >
              <SelectTrigger aria-label="슬롯 EZREMS 연결 후보">
                <SelectValue placeholder="검증된 후보 선택" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.resource_id} value={String(c.resource_id)}>
                    {c.dong} · {c.floor} · {c.ho2} · {c.sales_kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {target ? (
            !busy && candidates.length === 0 ? (
              <p role="status">
                선택한 프로젝트에 연결할 수 있는 호실이 없습니다. 프로젝트를 확인해 주세요.
              </p>
            ) : (
              <p>연결할 호실을 선택하고 연결 사유를 입력하세요.</p>
            )
          ) : (
            <p>EZREMS 프로젝트를 선택하면 연결할 호실을 불러옵니다.</p>
          )}
          <label className="grid gap-1">
            EZREMS 연결 사유
            <Input
              aria-label="슬롯 EZREMS 연결 사유"
              disabled={busy || intent !== null}
              value={reason}
              aria-invalid={reasonTooLong}
              aria-describedby={reasonTooLong ? reasonErrorId : undefined}
              onChange={(e) => {
                setReason(e.target.value);
                setIntent(null);
              }}
            />
          </label>
          {reasonTooLong && (
            <p id={reasonErrorId} role="alert">
              연결 사유는 500자 이내로 입력하세요.
            </p>
          )}
          <Button
            className="h-auto min-h-8 whitespace-normal"
            disabled={busy || !candidateId || !reason.trim() || reasonTooLong || intent !== null}
            onClick={() => {
              void review();
            }}
          >
            슬롯 연결 검토
          </Button>
        </>
      )}
      {intent && !blocked && (
        <fieldset
          aria-label="EZREMS 슬롯 최초 연결 최종 확인"
          className="space-y-3 rounded-md border p-4"
        >
          <legend>
            {props.roomName} · {props.unit.name} 슬롯에 연결할 호실을 확인하세요.
          </legend>
          <p>
            {intent.command.branch_code} · {intent.command.project_code} · {intent.command.dong} ·{' '}
            {intent.command.floor} · {intent.command.ho2} · {intent.command.reason}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={!allowed || busy || sending}
              onClick={() => {
                const confirmed = intent;
                clear();
                setContext(null);
                void send(confirmed);
              }}
            >
              슬롯 연결 저장
            </Button>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              onClick={() => {
                setIntent(null);
              }}
            >
              검토 취소
            </Button>
          </div>
        </fieldset>
      )}
      {feedback && !busy && !sending && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={!allowed || held !== null}
          onClick={() => {
            setDone(false);
            void reload();
          }}
        >
          연결 정보 다시 조회
        </Button>
      )}
      {held && !heldRefused && (
        <p role="alert">연결 결과를 확인하지 못했습니다. 같은 요청으로 다시 시도할 수 있습니다.</p>
      )}
      {held && heldRefused && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={!allowed || sending}
          onClick={() => {
            setHeld(null);
            setHeldRefused(false);
            setDone(false);
            setFeedback(
              '원래 요청을 폐기했습니다. 반영 여부는 다시 조회한 현재 연결로 확인하세요.',
            );
            void reload(true);
          }}
        >
          요청 폐기하고 현재 연결 다시 조회
        </Button>
      )}
      {held && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={!allowed || sending}
          onClick={() => {
            void send(held);
          }}
        >
          연결 요청 다시 시도
        </Button>
      )}
      {savedNotice && <p role="status">{savedNotice}</p>}
      {feedback && <p role="status">{feedback}</p>}
    </section>
  );
}
